import { Suspense } from "react";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button";
import ResendOtpButton from "@/components/buttons/ResendOtpButton";
import Loader from "@/components/helpers/Loader";
import VerificationInput from "@/components/inputs/VerificationInput";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { AuthModalState } from "@/@types/general";
import { ROUTES } from "@/config/routes";
import { usePostCheckCodeGetToken, usePostSendCode } from "@/hooks/api/login.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { onError } from "@/utils/onError";

import AuthSessions from "../AuthSessions";
import AuthTitle from "../AuthTitle.";
import { AuthSignInForm } from "../types";

const AuthLoginWithCodeModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useFormContext<AuthSignInForm>();
  const { mutate: mutateCode, isPending: isPendingCode } = usePostSendCode();
  const { data, mutate: mutateCheck } = usePostCheckCodeGetToken();

  const username = getValues("username");
  const { openModal } = useModalRouter();

  const onSubmit: SubmitHandler<AuthSignInForm> = ({ username, otp }): void => {
    mutateCheck(
      { username, code: otp },
      {
        onSuccess: async (res) => {
          if (res.data.is_active) {
            const { setToken } = await import("@/api/tokens.ts");
            setToken(res.data.token);
            navigate(ROUTES.GATE.PROFILES);

            return;
          }

          openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_SESSIONS], { token: res.data.token });
        },
        onError,
      },
    );
  };

  return (
    <div className="md:w-[26.25rem]">
      <AuthTitle
        goback
        title={t("auth.confirm-code")}
        subtitle={
          <Trans
            i18nKey={"auth.confirm-code-message"}
            values={{ value: username }}
            components={{
              1: <span className={"text-orange-1"} />,
            }}
          />
        }
      />
      <div className={"mt-10 flex flex-col items-center gap-[1.875rem]"}>
        <Controller
          name={"otp"}
          control={control}
          rules={{
            required: { value: true, message: t("Form.required") },
            validate: (value) => value.length === 6,
          }}
          render={({ field }) => (
            <VerificationInput {...field} onComplete={() => handleSubmit(onSubmit)()} error={!!errors.otp} />
          )}
        />
        <ResendOtpButton mutate={mutateCode} username={username} isPending={isPendingCode} />
      </div>
      <Button
        color={"white"}
        size="medium"
        onClick={() => navigate(`?auth=${AuthModalState.Password}`)}
        className={"mt-4"}
      >
        {t("Auth.enter-via-password")}
      </Button>
      {data && !data.data.is_active ? (
        <Suspense fallback={<Loader className={"fixed left-0 top-0 size-full bg-black bg-opacity-50"} />}>
          <AuthSessions token={data.data.token} />
        </Suspense>
      ) : null}
    </div>
  );
};

export default AuthLoginWithCodeModal;
